import type { AccountInsights, InstallBaseUpload, StatusValue } from '../api/types';

/**
 * Types and behavior for the Prepare/Propose content corpora.
 *
 * The DATA for every corpus below (gap rows, agenda, confirm items, discovery/prereq questions, next
 * steps, readiness checks, stakeholder priority, the question bank, IQ scenarios, recommended and
 * deep-dive questions) now lives in the database as `CampaignContentBlock` rows and is read through the
 * resolvers in `api/resolveContent.ts` — the database is the single source of truth. This file keeps
 * only the shared TYPES those resolvers annotate their results with, plus the two behavioral helpers
 * that cannot live in JSON (`recommendedKey`, and `IQ_SCENARIO_KEY`). The per-gap-row live-figure
 * functions live in `config/liveFigures.ts`.
 */

/** What a gap row can draw a live figure from. */
export interface LiveFigures {
  insights?: AccountInsights;
  installBase?: InstallBaseUpload | null;
}

export interface GapRowDef {
  /** Stable key: gap statuses persist against this, never a row position. */
  slug: string;
  category: string;
  /** Authored default, used when there is no live figure for this row. */
  currentState: string;
  /**
   * Live figure, preferring what was counted off the customer's own export and
   * falling back to whatever the backend knows. Behavior, not content: supplied from
   * `config/liveFigures.ts` keyed by `slug` (see `resolveGapRows`), never from the DB.
   */
  liveCurrentState: (live: LiveFigures) => string | null;
  gap: string;
  action: string;
  defaultStatus: StatusValue;
}

/** One session-plan row. Durations are summed to a single total; per-line times are never shown. */
export interface AgendaItem {
  title: string;
  desc: string;
  duration: string;
}

/** How a stakeholder's priority tier reads to the champion. */
export type StakeholderPriorityMap = Record<'must' | 'should' | 'nice', string>;

/**
 * Who the workshop needs in the room. Reference material, not a checklist.
 *
 * The priority is advice to the champion, so it is worded as advice: nobody is
 * mandatory, and calling a role "must have" invites an argument about whether
 * the workshop can run at all rather than a conversation about who to invite.
 */
export interface Stakeholder {
  label: string;
  role: 'must' | 'should' | 'nice';
  why: string;
}

/**
 * Questions to choose from for the first conversation.
 *
 * Grouped so an SE can take one or two from each area rather than working
 * through a script. Nothing here is compulsory; the point is to pick the
 * handful that fit the account and go in with them.
 */
export interface QuestionGroup {
  id: string;
  label: string;
  questions: string[];
  /**
   * Only shown when Cisco IQ entitlement matches. A customer who already has IQ
   * gets asked whether they use it; one who does not gets asked what they do
   * instead. Asking both sets makes the SE look like they did not check.
   */
  iq?: 'enabled' | 'not-enabled';
}

/** A question in the bank, looked up by the key it is stored under. */
export interface BankQuestionRef {
  key: string;
  question: string;
  group: string;
}

/**
 * How the account stands on Cisco IQ.
 *
 * Established on the proposal call and carried into the workshop, because it
 * decides how the deep-dive is run: a customer already living in IQ gets walked
 * through their own dashboard, one who has it but never opens it gets a guided
 * login, and one without it gets a demo on sample data.
 */
export type IqScenario = 'active' | 'enabled-unused' | 'not-enabled';

export const IQ_SCENARIO_KEY = '2-iq-scenario';

/** One Cisco IQ scenario option, with the deep-dive prep it implies. */
export interface IqScenarioDef {
  id: IqScenario;
  label: string;
  detail: string;
  /** What the SE should set up for the deep-dive as a result. */
  prep: string;
}

/**
 * A question worth asking on the proposal call. `when` narrows the two Inventory
 * variants so the SE is only ever asked the half that applies — the one whose
 * account already has Cisco IQ gets asked how they use it, the one without it
 * gets asked what they use instead.
 */
export interface RecommendedQuestion {
  id: string;
  category: string;
  question: string;
  why: string;
  when?: 'iq-enabled' | 'iq-not-enabled';
}

/** Where a recommended question's answer is stored. */
export function recommendedKey(id: string): string {
  return `2-rec.${id}`;
}

/** One guided deep-dive discovery question, grouped by the pillar the workshop assesses. */
export interface DeepDivePillarQuestion {
  pillar: string;
  question: string;
}
